import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, CheckCircle, Download, Sparkles, Leaf, Home } from "lucide-react";
import { trackEvent } from "@/components/GoogleAnalytics";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const LeadMagnetPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dismissedRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    const dismissed = sessionStorage.getItem("leadPopupDismissed");
    if (dismissed) {
      dismissedRef.current = true;
      return;
    }

    const showPopup = (label: string) => {
      if (dismissedRef.current) return;
      dismissedRef.current = true; // prevent re-triggers
      setIsVisible(true);
      trackEvent("lead_popup_shown", { category: "engagement", label });
    };

    const timer = setTimeout(() => showPopup("timer_trigger"), 25000);

    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 60) {
        showPopup("scroll_trigger");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem("leadPopupDismissed", "true");
    trackEvent("lead_popup_dismissed", { category: "engagement" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    try {
      await supabase.functions.invoke("submit-intake-request", {
        body: {
          name: name.trim() || "Checklist Download",
          email: email.trim(),
          phone: "",
          service: "free-checklist",
          message: "Downloaded free organizing checklist via popup",
        },
      });

      trackEvent("lead_magnet_converted", {
        category: "conversion",
        label: "organizing_checklist",
      });

      setIsSubmitted(true);
      sessionStorage.setItem("leadPopupDismissed", "true");
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again or call us at 616.727.0343",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  const checklistItems = [
    { icon: Home, text: "Kitchen & pantry deep-reset guide" },
    { icon: Leaf, text: "Seasonal closet rotation system" },
    { icon: Home, text: "Home office productivity layout" },
    { icon: Leaf, text: "30-day clutter-free maintenance plan" },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={handleDismiss}
    >
      <div
        className="relative bg-background rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-accent/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          className="absolute top-3 right-3 z-20 p-2 rounded-full bg-muted hover:bg-muted-foreground/20 transition-colors shadow-md"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>

        {/* Top decorative gradient bar */}
        <div className="h-2 bg-gradient-to-r from-accent via-primary to-accent" />

        {isSubmitted ? (
          <div className="p-8 md:p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-accent" />
            </div>
            <h3 className="text-2xl md:text-3xl font-serif mb-3 text-foreground">You're All Set!</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Check your inbox for your free organizing checklist. We'll also send a few expert tips to help you get started!
            </p>
            <Button onClick={handleDismiss} variant="outline" className="rounded-full px-8">
              Close
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-5">
            {/* Left accent panel */}
            <div className="hidden md:flex md:col-span-2 bg-gradient-to-br from-accent/15 via-primary/10 to-accent/5 flex-col items-center justify-center p-6 text-center">
              <div className="w-20 h-20 rounded-2xl bg-accent/20 flex items-center justify-center mb-4 rotate-3">
                <Download className="w-10 h-10 text-accent" />
              </div>
              <p className="text-sm font-medium text-foreground/80 leading-relaxed">
                Join <span className="font-bold text-accent">200+</span> Michigan families who've transformed their homes
              </p>
            </div>

            {/* Right content panel */}
            <div className="md:col-span-3 p-6 md:p-8">
              <div className="inline-flex items-center gap-1.5 bg-accent/10 text-accent text-xs font-bold tracking-wider px-3 py-1.5 rounded-full mb-4 uppercase">
                <Sparkles className="w-3.5 h-3.5" />
                Free Download
              </div>

              <h3 className="text-xl md:text-2xl font-serif mb-2 text-foreground leading-tight">
                The Ultimate Room-by-Room Organizing Checklist
              </h3>
              <p className="text-muted-foreground text-sm mb-5">
                Your step-by-step guide to a calmer, clutter-free home.
              </p>

              <div className="space-y-2.5 mb-6">
                {checklistItems.map((item) => (
                  <div key={item.text} className="flex items-center gap-2.5">
                    <div className="w-6 h-6 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-3 h-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground/90">{item.text}</span>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-2.5">
                <Input
                  placeholder="First name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-11 rounded-xl bg-muted/50 border-border/60 focus:border-accent"
                />
                <Input
                  type="email"
                  placeholder="Email address *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 rounded-xl bg-muted/50 border-border/60 focus:border-accent"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-accent hover:bg-accent/90 text-white h-12 text-base font-semibold rounded-xl shadow-lg shadow-accent/25 transition-all hover:shadow-xl hover:shadow-accent/30"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Get My Free Checklist
                    </>
                  )}
                </Button>
              </form>

              <p className="text-[11px] text-muted-foreground text-center mt-3 opacity-70">
                No spam, ever. Unsubscribe anytime.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadMagnetPopup;
